import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ChatBlockResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseChatBlocksReturn {
  blocks: ChatBlockResponse[];
  loading: boolean;
  error: Error | null;
  /** True when this account is on the viewer's block list. */
  isBlocked: (userId: string) => boolean;
  block: (userId: string) => Promise<void>;
  unblock: (userId: string) => Promise<void>;
  /** True while any block/unblock is in flight — for disabling the control. */
  mutating: boolean;
}

/**
 * The viewer's blocked-accounts list, shared by every surface that shows or
 * changes block state.
 *
 * It is one cached query rather than a fetch per surface for a reason: the
 * member popover, the profile card, the guild member list, and the settings
 * panel can all be on screen at once, and a per-component fetch made them
 * disagree — a user unblocked in settings still read as blocked in the popover
 * behind it. Mutations invalidate the single key, so every surface re-reads.
 */
// Built once at module scope: `queryKeys.chat.blocks()` returns a fresh array on
// every call, so building it in the body gave every memo below a new dependency
// each render and none of them ever memoized.
const BLOCKS_KEY = queryKeys.chat.blocks();

export function useChatBlocks(): UseChatBlocksReturn {
  const queryClient = useQueryClient();
  const blocksKey = BLOCKS_KEY;

  const { data, isLoading, error } = useQuery({
    queryKey: blocksKey,
    queryFn: () => api.listChatBlockedUsers(),
  });

  const blocks = useMemo(() => data?.blocks ?? [], [data]);

  const blockedIds = useMemo(() => new Set(blocks.map((b) => b.blockedId)), [blocks]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: blocksKey });
    // Blocking hides the other party's messages, so the conversation the viewer
    // is looking at is stale the moment the block lands.
    void queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
  }, [queryClient, blocksKey]);

  const blockMut = useMutation({
    mutationFn: (userId: string) => api.blockChatUser(userId),
    onSuccess: invalidate,
  });

  const unblockMut = useMutation({
    mutationFn: (userId: string) => api.unblockChatUser(userId),
    onSuccess: invalidate,
  });

  return {
    blocks,
    loading: isLoading,
    error: (error as Error) ?? null,
    isBlocked: useCallback((userId: string) => blockedIds.has(userId), [blockedIds]),
    block: useCallback(
      async (userId: string) => {
        await blockMut.mutateAsync(userId);
      },
      [blockMut],
    ),
    unblock: useCallback(
      async (userId: string) => {
        await unblockMut.mutateAsync(userId);
      },
      [unblockMut],
    ),
    mutating: blockMut.isPending || unblockMut.isPending,
  };
}
